import apiServices from "../apiServices";
import { validateOrThrowApiResponse } from "../response-validator";

type SituationChoicePayload = {
    id?: number;
    choice: string;
    is_correct: boolean;
    image_choice: string | null;
    sound_choice?: string | null;
};

type SituationQuestionPayload = {
    no: number;
    expected_time?: number;
    question: string;
    question_position: string;
    hint?: string | null;
    image_question: string | null;
    sound_question?: string | null;
    image_hint?: string | null;
    shuffle_answer?: boolean;
    sound_hint?: string | null;
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
    audio_question?: boolean;
    audio_choice?: boolean;
    audio_assistant_voice?: boolean;
    audio_background?: boolean;
    choices: SituationChoicePayload[];
};

type CreateSituationPayload = {
    gameId: number | string;
    questions: SituationQuestionPayload[];
};

type UpdateQuestionSituationPayload = {
    situationQuestionId: number | string;
    no?: number;
    expected_time?: number;
    question?: string;
    question_position?: string;
    hint?: string | null;
    image_question?: string | null;
    sound_question?: string | null;
    image_hint?: string | null;
    shuffle_answer?: boolean;
    sound_hint?: string | null;
    skill_listening_speaking?: boolean;
    skill_lang_nature?: boolean;
    skill_reading?: boolean;
    skill_writing?: boolean;
    audio_question?: boolean;
    audio_choice?: boolean;
    audio_assistant_voice?: boolean;
    audio_background?: boolean;
};

type UpdateChoiceSituationPayload = {
    situationQuestionId: number | string;
    choices: SituationChoicePayload[];
};

type CreateChoiceSituationPayload = {
    situationQuestionId: number | string;
    choices: Omit<SituationChoicePayload, "id">[];
};

type CreateChoiceSituationResponse = {
    id_question_multiple?: number;
    choices?: Array<{
        id?: number;
        choice?: string | null;
        is_correct?: boolean;
        sound_choice?: string | null;
        image_choice?: string | null;
        create_at?: string;
        update_at?: string;
    }>;
};

type UpdateExerciseNameAndSuggestionPayload = {
    gameId: number | string;
    exercise_name?: string;
    suggestion?: string;
    description?: string;
};

type DeleteQuestionSituationPayload = {
    situationQuestionId: number | string;
};

type DeleteChoiceSituationPayload = {
    situationQuestionId: number | string;
    choiceIds: Array<number | string>;
};

type SituationAssetPayload = {
    asset: string;
    position: string;
};

type CreateSituationAssetsPayload = {
    gameId: number | string;
    assets: SituationAssetPayload[];
};

type UpdateSituationAssetsPayload = {
    assetId: number | string;
    asset: string;
    position: string;
};

type HardDeleteSituationAssetsPayload = {
    assetId: number | string;
};

const QUESTION_SITUATION_MEDIA_FIELDS = [
    "sound_question",
    "image_question",
    "hint",
    "sound_hint",
    "image_hint",
] as const;

type QuestionSituationMediaField = typeof QUESTION_SITUATION_MEDIA_FIELDS[number];

type HardDeleteQuestionMediaFieldPayload = {
    situationQuestionId: number | string;
    field: QuestionSituationMediaField;
};

const CHOICE_SITUATION_MEDIA_FIELDS = ["sound_choice", "image_choice"] as const;

type ChoiceSituationMediaField = typeof CHOICE_SITUATION_MEDIA_FIELDS[number];

type HardDeleteChoiceMediaFieldPayload = {
    situationChoiceId: number | string;
    field: ChoiceSituationMediaField;
};

const situationAPI = {

    createSituation(payload: CreateSituationPayload) {
        return apiServices
            .post(`/api/game/${payload.gameId}/situationQuestions`, {
                questions: payload.questions,
            }, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateQuestionSituation(payload: UpdateQuestionSituationPayload) {
        const requestBody: {
            no?: number;
            expected_time?: number;
            question?: string;
            question_position?: string;
            hint?: string | null;
            image_question?: string | null;
            sound_question?: string | null;
            image_hint?: string | null;
            shuffle_answer?: boolean;
            sound_hint?: string | null;
            skill_listening_speaking?: boolean;
            skill_lang_nature?: boolean;
            skill_reading?: boolean;
            skill_writing?: boolean;
            audio_question?: boolean;
            audio_choice?: boolean;
            audio_assistant_voice?: boolean;
            audio_background?: boolean;
        } = {};

        if (payload.no !== undefined) requestBody.no = payload.no;
        if (payload.expected_time !== undefined) requestBody.expected_time = payload.expected_time;
        if (payload.question !== undefined) requestBody.question = payload.question;
        if (payload.question_position !== undefined) requestBody.question_position = payload.question_position;
        if (payload.hint !== undefined) requestBody.hint = payload.hint;
        if (payload.image_question !== undefined) requestBody.image_question = payload.image_question;
        if (payload.sound_question !== undefined) requestBody.sound_question = payload.sound_question;
        if (payload.image_hint !== undefined) requestBody.image_hint = payload.image_hint;
        if (payload.shuffle_answer !== undefined) {
            requestBody.shuffle_answer = payload.shuffle_answer;
        }
        if (payload.sound_hint !== undefined) requestBody.sound_hint = payload.sound_hint;
        if (payload.skill_listening_speaking !== undefined) {
            requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        }
        if (payload.skill_lang_nature !== undefined) {
            requestBody.skill_lang_nature = payload.skill_lang_nature;
        }
        if (payload.skill_reading !== undefined) {
            requestBody.skill_reading = payload.skill_reading;
        }
        if (payload.skill_writing !== undefined) {
            requestBody.skill_writing = payload.skill_writing;
        }
        if (payload.audio_question !== undefined) {
            requestBody.audio_question = payload.audio_question;
        }
        if (payload.audio_choice !== undefined) {
            requestBody.audio_choice = payload.audio_choice;
        }
        if (payload.audio_assistant_voice !== undefined) {
            requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        }
        if (payload.audio_background !== undefined) {
            requestBody.audio_background = payload.audio_background;
        }

        return apiServices
            .put(`/api/situationQuestions/${payload.situationQuestionId}`, requestBody, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateQuestionSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateChoiceSituation(payload: UpdateChoiceSituationPayload) {
        return apiServices
            .put(`/api/situationQuestions/${payload.situationQuestionId}/choices`, {
                choices: payload.choices,
            }, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateChoiceSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    createChoiceSituation(payload: CreateChoiceSituationPayload) {
        return apiServices
            .post(`/api/situationQuestions/${payload.situationQuestionId}/choices`, {
                choices: payload.choices,
            }, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse<CreateChoiceSituationResponse>(res))
            .catch((err) => {
                console.log("Error createChoiceSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateExerciseNameAndSuggestion(payload: UpdateExerciseNameAndSuggestionPayload) {
        const requestBody: { exercise_name?: string; suggestion?: string; description?: string } = {};
        if (payload.exercise_name !== undefined) {
            requestBody.exercise_name = payload.exercise_name;
        }
        if (payload.suggestion !== undefined) {
            requestBody.suggestion = payload.suggestion;
        }
        if (payload.description !== undefined) {
            requestBody.description = payload.description;
        }

        return apiServices
            .patch(`/api/game/${payload.gameId}/exerciseNameAndSuggestion`, requestBody, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateExerciseNameAndSuggestion:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    insertImageGameSituation(payload: { gameId: number | string; file: File | Blob; filename?: string }) {
        const formData = new FormData();
        if (payload.filename) {
            formData.append("other_image", payload.file, payload.filename);
        } else {
            formData.append("other_image", payload.file as any);
        }

        return apiServices
            .patch(`/api/game/${payload.gameId}/other-image`, formData, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error insertImageGameSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    deleteQuestionSituation(payload: DeleteQuestionSituationPayload) {
        return apiServices
            .delete(`/api/situationQuestions/${payload.situationQuestionId}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error deleteQuestionSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    deleteChoiceSituation(payload: DeleteChoiceSituationPayload) {
        return apiServices
            .post(`/api/situationQuestions/${payload.situationQuestionId}/choices/delete`, {
                choiceIds: payload.choiceIds,
            }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error deleteChoiceSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    deleteOtherImageGameSituation(payload: { gameId: number | string }) {
        return apiServices
            .delete(`/api/game/${payload.gameId}/other-image`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error deleteOtherImageGameSituation:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    createSituationAssets(payload: CreateSituationAssetsPayload) {
        return apiServices
            .post(`/api/game/${payload.gameId}/situation-assets`, {
                assets: payload.assets,
            }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createSituationAssets:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateSituationAssets(payload: UpdateSituationAssetsPayload) {
        return apiServices
            .patch(`/api/situation-assets/${payload.assetId}`, {
                asset: payload.asset,
                position: payload.position,
            }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateSituationAssets:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteSituationAssets(payload: HardDeleteSituationAssetsPayload) {
        return apiServices
            .delete(`/api/situation-assets/${payload.assetId}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteSituationAssets:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteQuestionMediaFileAndHint(payload: HardDeleteQuestionMediaFieldPayload) {
        if (!QUESTION_SITUATION_MEDIA_FIELDS.includes(payload.field as any)) {
            return Promise.resolve({
                status: "failed",
                errMessage: `Invalid field ${payload.field}`,
                error: null,
            });
        }

        return apiServices
            .patch(`/api/situationQuestions/${payload.situationQuestionId}/hard-delete-field`, {
                field: payload.field,
            }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteQuestionMediaFileAndHint:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteChoiceMediaFileAndHint(payload: HardDeleteChoiceMediaFieldPayload) {
        if (!CHOICE_SITUATION_MEDIA_FIELDS.includes(payload.field as any)) {
            return Promise.resolve({
                status: "failed",
                errMessage: `Invalid field ${payload.field}`,
                error: null,
            });
        }

        return apiServices
            .patch(`/api/situationChoices/${payload.situationChoiceId}/hard-delete-field`, {
                field: payload.field,
            }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteChoiceMediaFileAndHint:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

}

export default situationAPI;